// @flow
import * as React from 'react';
import {
  Button, Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { useContext, useEffect, useRef, useState } from 'react';
import {LoadingButton} from "@mui/lab";
import {
  CallHistoryItemsQuery,
  useSaveCallHistoryMutation
} from '../../../../../shared';
import { getUserName } from '@coverright/utils';
import { SelectInput } from '@coverright/ui/inputs';
import { stringifyCallResult, stringifyCallType } from './call-card';
import { Client } from '../../../../../shared/api/use-client-subscription';
import { CallHistoryCallType, CallHistoryResultInput } from '@coverright/data-access/types/enrollment';
import { ModalCloseButton } from '@coverright/ui/shared';

type Call = CallHistoryItemsQuery['callHistoryItems']['data'][0]

type Props = {
  client: Partial<Client>;
  call?: Call;
  open: boolean;
  onClose: () => void;
};
export const CallFormModal = ({call, onClose, open, client}: Props) => {
  const [value, setValue] = useState<{callType?: CallHistoryCallType | null, result?: CallHistoryResultInput | null}>(
    {callType: call?.callType, result: call?.result as unknown as CallHistoryResultInput}
  )

  const close = () => {
    setValue({})
    onClose()
  }

  useEffect(() => {
    open && call && setValue({callType: call.callType, result: call.result as unknown as CallHistoryResultInput})
  }, [open, call])

  const [save, {loading}] = useSaveCallHistoryMutation({ onCompleted: close })

  if (!client) return null;

  const submit = () => {
    save({
      variables: {
        input: {
          ...value,
          phoneNumber: call?.phoneNumber || client.phoneNumber!,
          clientId: client.id,
          id: call?.id
        }
      }
    })
  }

  return (
    <Dialog
      sx={{'& .MuiPaper-root': {width: 500}}}
      open={open}
      onClose={close}
    >
      <DialogTitle component={Typography} variant={'h5'}>Meeting with {getUserName(client)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1} >
          <SelectInput value={value.callType || ''} hideTick onChange={event => {
            setValue(prev => ({...prev, callType: event.target.value as CallHistoryCallType}))
          }} label={'Call Type'}>
            <MenuItem value={CallHistoryCallType.Inbound}>{stringifyCallType(CallHistoryCallType.Inbound)}</MenuItem>
            <MenuItem value={CallHistoryCallType.Outbound}>{stringifyCallType(CallHistoryCallType.Outbound)}</MenuItem>
          </SelectInput>

          <SelectInput value={value.result || ''} hideTick onChange={event => {
            setValue(prev => ({...prev, result: event.target.value as CallHistoryResultInput}))
          }} label={'Result'}>
            <MenuItem value={CallHistoryResultInput.SuccessfulSeeNotesForCallDetails}>{stringifyCallResult(CallHistoryResultInput.SuccessfulSeeNotesForCallDetails)}</MenuItem>
            <MenuItem value={CallHistoryResultInput.SuccessfulBookedCall}>{stringifyCallResult(CallHistoryResultInput.SuccessfulBookedCall)}</MenuItem>
            <MenuItem value={CallHistoryResultInput.SuccessfulWarmTransfer}>{stringifyCallResult(CallHistoryResultInput.SuccessfulWarmTransfer)}</MenuItem>
            <MenuItem value={CallHistoryResultInput.UnsuccessfulInvalidNumber}>{stringifyCallResult(CallHistoryResultInput.UnsuccessfulInvalidNumber)}</MenuItem>
            <MenuItem value={CallHistoryResultInput.UnsuccessfulLeftText}>{stringifyCallResult(CallHistoryResultInput.UnsuccessfulLeftText)}</MenuItem>
            <MenuItem value={CallHistoryResultInput.UnsuccessfulLeftVm}>{stringifyCallResult(CallHistoryResultInput.UnsuccessfulLeftVm)}</MenuItem>
            <MenuItem value={CallHistoryResultInput.UnsuccessfulLeftEmail}>{stringifyCallResult(CallHistoryResultInput.UnsuccessfulLeftEmail)}</MenuItem>
          </SelectInput>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="primary" variant={'outlined'}>
          Close
        </Button>
        <LoadingButton loading={loading}
                       onClick={submit}
                       disabled={!value.result || !value.callType}
                       color={'primary'}
                       variant={'contained'}>
          {call ? 'Save' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};


interface ContextProps {
  open: (c: Partial<Client>, ca?: Call, closeCallback?: () => void) => void;
}

const CallFormContext = React.createContext<ContextProps>({
  open: () => {},
})

export const useCallForm = () => useContext(CallFormContext)

export function withCallForm<P>(WrappedComponent: React.FC<P>) {
  return (props: P) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [client, setClient] = useState<Partial<Client>>();
    const [call, setCall] = useState<Call>();
    const closeCallback = useRef<any>();

    const open = (c: Partial<Client>, ca?: Call, callback?: () => void) => {
      setClient(c)
      setCall(ca)
      if (callback) {
        closeCallback.current = callback
      }
      setDialogOpen(true)
    }

    const handleClose = () => {
      setDialogOpen(false);
      closeCallback.current && closeCallback.current();
    }

    return <CallFormContext.Provider value={{open}}>
      <CallFormModal open={!!client && dialogOpen}
                     onClose={handleClose}
                     call={call}
                     client={client!} />
      <WrappedComponent {...props} />
    </CallFormContext.Provider>
  }
}
